import { useCallback, useEffect, useState } from "react";
import { useFormContext } from "react-hook-form";
import { useIntl } from "react-intl";

import { deleteFile } from "api";
import errorToast from "helpers/errorToast";
import { AttachmentBucket } from "types/Common";

import { useFileUpload } from "@RequestFormV2/sections/Provider/hooks/useFileUpload";
import { BucketFileEntry, BucketSelectedFile, fromBucketFileEntry } from "@RequestFormV2/sections/Provider/hooks/attachmentTypes";

type BucketAttachmentHandlers = {
  onAddFile: (file: BucketSelectedFile, bucketName: string) => void;
  onDeleteFile: (file: { id: number }) => void;
};

export function useBucketAttachment(bucket: AttachmentBucket, { onAddFile, onDeleteFile }: BucketAttachmentHandlers) {
  const intl = useIntl();
  const { getValues, clearErrors } = useFormContext();
  const { upload } = useFileUpload();

  const [selectedFile, setSelectedFile] = useState<BucketSelectedFile | null>(() => {
    const files: BucketFileEntry[] = getValues("bucket_attachments") || [];
    const match = files.find((f) => f.id === bucket.id);
    return match ? fromBucketFileEntry(match) : null;
  });

  useEffect(() => {
    if (selectedFile) clearErrors("bucket_attachments");
    // Only clear validation when hydrating an existing attachment on mount.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);
  const [uploadInProgress, setUploadInProgress] = useState(false);
  const [uploadingFileName, setUploadingFileName] = useState<string | undefined>();
  const [uploadError, setUploadError] = useState(false);
  const [uploadErrorMsg, setUploadErrorMsg] = useState("");
  const [isDeleting, setIsDeleting] = useState(false);

  const handleUpload = useCallback(
    (file: File) => {
      if (selectedFile) onDeleteFile(selectedFile);
      setUploadError(false);
      setUploadErrorMsg("");
      setSelectedFile(null);
      setUploadingFileName(file.name);
      setUploadInProgress(true);

      upload(file, () => {})
        .then((data) => {
          const newFile: BucketSelectedFile = {
            id: bucket.id,
            reference: data.reference_number,
            filename: data.name.split("/").pop() ?? data.name,
            url: data.url,
          };
          setSelectedFile(newFile);
          onAddFile(newFile, bucket.bucket_name);
        })
        .catch((error) => {
          errorToast(error);
          setUploadError(true);
          setUploadErrorMsg(
            error.message ||
              intl.formatMessage({
                id: "request-form.provider.uploadFreightDocuments.uploadFailed",
                defaultMessage: "Upload failed. Please try again",
              }),
          );
        })
        .finally(() => {
          setUploadInProgress(false);
          setUploadingFileName(undefined);
        });
    },
    [selectedFile, onDeleteFile, upload, bucket.id, bucket.bucket_name, onAddFile, intl],
  );

  const handleDelete = useCallback(() => {
    if (!selectedFile?.reference || isDeleting) return;
    setIsDeleting(true);
    deleteFile(selectedFile.reference)
      .then((res: { status: number }) => {
        if (res.status === 204) {
          onDeleteFile(selectedFile);
          setSelectedFile(null);
        }
      })
      .catch((err: { response?: { status: number } }) => {
        if (err.response?.status === 404) {
          onDeleteFile(selectedFile);
          setSelectedFile(null);
          return;
        }
        errorToast(err, "Could not delete file, please try again later");
      })
      .finally(() => {
        setIsDeleting(false);
      });
  }, [selectedFile, onDeleteFile, isDeleting]);

  const handleFileSelect = useCallback(
    (event: React.ChangeEvent<HTMLInputElement>) => {
      const file = event.target.files?.[0];
      if (file) handleUpload(file);
      event.target.value = "";
    },
    [handleUpload],
  );

  return {
    selectedFile,
    uploadingFileName,
    uploadInProgress,
    uploadError,
    uploadErrorMsg,
    isDeleting,
    handleFileSelect,
    handleDelete,
    isVisible: bucket.is_active,
    label: bucket.bucket_name,
    isMandatory: bucket.is_mandatory,
  };
}
